import { Bug, Layers, Map, Package, Send, Settings, ShieldCheck, Truck, Users, Wrench } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';
import { PERMISSIONS } from 'utils/permissionUtils';

export interface NavEntry {
  key: string;
  /** Resolver rather than a key: the repo's i18n guard only allows string-literal translations. */
  label: (t: TFunction) => string;
  to: string;
  permission: string;
  icon: LucideIcon;
  /** Key of the entry this one is nested under — renders indented, inside a collapsible group. */
  parent?: string;
  /**
   * Makes this entry a group and labels the head line the design injects as its first child.
   * The head links to the section itself, so the parent row and the head are both active on it.
   */
  headLabel?: (t: TFunction) => string;
}

// Declaration order is the render order. No secondary sort: the mock-ups sorted on a partial
// `navOrder` and any missing key scored -1, which hoisted it to the top of the sidebar.
export const NAV_ENTRIES: NavEntry[] = [
  { key: 'users', label: (t) => t('Users'), to: '/admin', permission: PERMISSIONS.DASHBOARD_USERS_VIEW, icon: Users },
  { key: 'technicians', label: (t) => t('Technicians'), to: '/admin/technicians', permission: PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW, icon: Wrench, headLabel: (t) => t('Technician list') },
  // Indented under Technicians, as the mock-up's sidebar draws it: the follow-up is about the
  // pool, not about tickets. Named « Demandes d'intervention » and not « Suivi techniciens » —
  // the back office and the mobile app must call the same object the same thing.
  { key: 'interventionRequests', label: (t) => t('Intervention requests'), to: '/admin/intervention-requests', permission: PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW, icon: Send, parent: 'technicians' },
  { key: 'issues', label: (t) => t('Issues'), to: '/admin/issues', permission: PERMISSIONS.DASHBOARD_ISSUES_VIEW, icon: Bug, headLabel: (t) => t('View all') },
  { key: 'groups', label: (t) => t('Groups'), to: '/admin/groups', permission: PERMISSIONS.DASHBOARD_GROUPS_VIEW, icon: ShieldCheck, parent: 'issues' },
  // No standalone /admin/referentials page exists, so the group row links to its first screen
  // rather than to a dead route.
  { key: 'referentials', label: (t) => t('Referentials'), to: '/admin/referentials/specialties', permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, icon: Layers, headLabel: (t) => t('Specialties and expertises') },
  { key: 'geography', label: (t) => t('Sectorisation'), to: '/admin/referentials/geography', permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, icon: Map, parent: 'referentials' },
  { key: 'quotas', label: (t) => t('Quotas'), to: '/admin/quotas', permission: PERMISSIONS.DASHBOARD_QUOTAS_VIEW, icon: Settings },
  { key: 'stocks', label: (t) => t('Stocks'), to: '/admin/stocks', permission: PERMISSIONS.DASHBOARD_STOCKS_VIEW, icon: Package },
  { key: 'deployments', label: (t) => t('Deployments'), to: '/admin/deployments', permission: PERMISSIONS.DASHBOARD_DEPLOYMENTS_VIEW, icon: Truck },
];

/**
 * Entry matching the current route. Longest `to` wins so a sub-route keeps its section active
 * (`/admin/issues/LS-2510` → Issues) without `/admin` swallowing every path.
 */
export const matchNavEntry = (pathname: string, entries: NavEntry[] = NAV_ENTRIES): NavEntry | null =>
  entries
    .filter((entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`))
    .reduce<NavEntry | null>((best, entry) => (!best || entry.to.length > best.to.length ? entry : best), null);

// A hand-typed URL can carry malformed percent-encoding (`%A`), on which decodeURIComponent
// throws — the breadcrumb must show the raw segment, never take the shell down.
const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch (error) {
    return segment;
  }
};

/** Path segments below the matched entry, e.g. `/admin/issues/LS-2510` → `['LS-2510']`. */
export const trailingSegments = (pathname: string, entry: NavEntry | null): string[] =>
  entry ? pathname.slice(entry.to.length).split('/').filter(Boolean).map(decodeSegment) : [];
